// BuildingShared.ts — the shared VALUE classes for the Building/Room/Window/Door modifier system
// (Buildings Set1, `Docs/Plans/Buildings/Set1_BuildingRoomSystem.md` §2.1).
//
// These classes are pure data (no `@Field`/`@MGLClass` — they live nested inside `Mod_*` classes
// whose `ObjModifier` is itself a plain-object field of `MapObject`). Every nullable leaf
// participates in the §3 fallback chain: `null` = "inherit from the next source down the chain".

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export enum BuildingShapeSourceKind { shape = "shape", mesh = "mesh", path = "path" }

/** References how a Room's footprint is sourced from the host `MapObject`'s authored geometry:
 *  its basic shape, one of its meshes, or one of its paths. `null` on `Mod_Room.shapeSource`
 *  auto-resolves shape → first mesh → first path. */
export class ShapeSourceRef {
	constructor(data?: Partial<ShapeSourceRef>) { Object.assign(this, data); }

	kind: BuildingShapeSourceKind;
	/** Index into `obj.meshes` / `obj.paths`. Ignored for kind "shape". Null = index 0. */
	index?: number|n;
	/** For kind "path" only: prefer resolving by `ObjPath.path_id` when set (stable across reorders). */
	pathID?: string|n;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export enum DoorSwing {
	inLeft = "inLeft",
	inRight = "inRight",
	outLeft = "outLeft",
	outRight = "outRight",
}

/** Door hand, per the standard rule judged from OUTSIDE facing the door: `Left`/`Right` = hinge side,
 *  `in` = swings in (away from you), `out` = swings out (toward you). inLeft=LHH, inRight=RHH,
 *  outLeft=LHR, outRight=RHR. */
export const DoorSwingInfo = `
	Left/right: Which side the hinges are on (always judged from OUTSIDE the room, looking at the door).
	In/out: Whether the door swings "in" (into the room) or "out" (out of the room) when opened.
	* inLeft = left-hand hinge (LHH) = hinges on the left (for outside observer), door swings into room
	* inRight = right-hand hinge (RHH) = hinges on the right (for outside observer), door swings into room
	* outLeft = left-hand reverse (LHR) = hinges on the left (for outside observer), door swings out of room
	* outRight = right-hand reverse (RHR) = hinges on the right (for outside observer), door swings out of room
`.AsMultiline(0);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export enum RoofStyle { flat = "flat", hip = "hip", gable = "gable" }

/** Every field is a recipe-ID from the `GetProcMatRecipes(mapID)` catalogue, or null = inherit. */
export class BuildingMaterials {
	constructor(data?: Partial<BuildingMaterials>) { Object.assign(this, data); }

	floorTop?: string|n;
	floorUnderside?: string|n;
	floorEdge?: string|n;
	ceiling?: string|n;
	wallExterior?: string|n;
	wallInterior?: string|n;
	wallTop?: string|n;
	wallReveal?: string|n;   // jambs/sill/head inside an opening
	roofSurface?: string|n;
	roofFascia?: string|n;
	roofSoffit?: string|n;
	doorFrame?: string|n;
	doorSlab?: string|n;
	doorHardware?: string|n;
	windowFrame?: string|n;
	windowGlass?: string|n;
}
